import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { teamGroupKeys } from "@/hooks/queryKeys";

interface TeamGroupOption {
  id: string;
  name: string;
}

/**
 * useTeamGroups (TES-1783) — active groups (Fachbereiche) of a team, for assignment pickers.
 *
 * Reads via the user-JWT-scoped client so RLS (teams:view:limited + team-scope, Migration 325)
 * gates which groups are returned. Used to assign a Prüfung/Prüfplan to a group instead of a
 * single user (assigned_group_id, Migration 326).
 */
export function useTeamGroups(teamId: string | null | undefined, enabled = true) {
  const query = useQuery({
    queryKey: teamGroupKeys.lists(teamId),
    queryFn: async (): Promise<TeamGroupOption[]> => {
      if (!teamId) return [];
      const { data, error } = await supabase
        .from("team_group")
        .select("id, name")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: enabled && !!teamId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
